import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import Layout from 'containers/Layout'
import moment from 'moment'
import apiGet from 'lib/network/apiGet'
import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'
import Popover from 'components/molecules/Popover'
import { formateDate } from 'utils'
import notify from 'components/atoms/Notify'
import { IoCalendar } from 'react-icons/io5'
import { DateRangePicker } from 'react-date-range'
import { Tooltip } from 'antd'
import apiPost from 'lib/network/apiPost'
import { ChevronDownIcon } from '@heroicons/react/outline'
import PageFooter from 'components/paginationComponent'
import Selection from 'components/molecules/Selection'
import apiGetBlob from 'lib/network/apiGetBlob'

import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

const OfficeSalesReport = () => {
  const { t } = useTranslation()
  const userFromStore = useSelector(state => get(state, 'user.user'))
  const isSuperCompanyUser = Boolean(userFromStore?.superCompanyUser)

  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null })
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [pagination, setPagination] = useState({})
  const [reports, setReports] = useState([])
  const [branches, setBranches] = useState([])
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [loadingBranches, setLoadingBranches] = useState(false)

  const popover = useRef()

  useEffect(() => {
    fetchBranches()
    fetchReports()
  }, [])

  const fetchBranches = async () => {
    try {
      setLoadingBranches(true)
      const response = await apiGet('branches', { pageSize: 200 })
      const branchList = (response?.data?.branch || []).map(branch => ({
        id: branch._id,
        name: branch.name,
      }))
      setBranches(branchList)

      if (!isSuperCompanyUser && branchList.length === 1) {
        setSelectedBranch(branchList[0].id)
      }
    } catch (error) {
      notify.error(t('salesReports.messages.fetchBranchesFailed'))
      setBranches([])
    } finally {
      setLoadingBranches(false)
    }
  }

  const fetchReports = async (page = 1) => {
    if (page === 1) {
      setLoading(true)
    }

    const response = await apiGet('company-dashboard/reports', {
      pageNumber: page,
      pageSize: 20,
    })

    setReports(page === 1 ? response.data.report : [...reports, ...response.data.report])
    setPagination(response?.data?.pagination || {})
    setLoading(false)
  }

  const handleSelect = range => {
    setDateRange({
      startDate: moment(range.selection.startDate),
      endDate: moment(range.selection.endDate),
    })
  }

  const handleReportDownload = async (reportId, format = 'pdf') => {
    if (!reportId) return

    try {
      const blob = await apiGetBlob(`company-dashboard/reports/${reportId}/download?format=${format}`)
      if (!blob) return

      const fileExtension = format === 'excel' || format === 'xlsx' ? 'xlsx' : 'pdf'
      const fileName = `sales_report_${reportId || 'download'}.${fileExtension}`
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading report:', error)
    }
  }

  const generateSalesReport = async () => {
    try {
      setGenerating(true)

      const payload = {
        branch: selectedBranch,
        type: 'sales',
        paymentStatus: ['paid'],
        bookingStatus: ['confirmed', 'completed'],
        reportStartDate: dateRange.startDate ? dateRange.startDate.format('YYYY-MM-DD') : null,
        reportEndDate: dateRange.endDate ? dateRange.endDate.format('YYYY-MM-DD') : null,
      }

      const response = await apiPost('company-dashboard/sales-report', payload)

      if (response.success) {
        notify.success(t('officeSalesReport.title'), t('officeSalesReport.messages.reportGeneratedSuccess'))
        setReports([])
        fetchReports()

        if (response.data?._id || response.data?.id) {
          const reportId = response.data._id || response.data.id
          const format = response.data?.pdfUrl ? 'pdf' : 'excel'
          handleReportDownload(reportId, format)
        }

        setSelectedBranch(isSuperCompanyUser ? null : selectedBranch)
        setDateRange({ startDate: null, endDate: null })
      } else {
        notify.error(response?.data?.message || t('officeSalesReport.messages.reportGenerationFailed'))
      }
    } catch (error) {
      notify.error(t('officeSalesReport.messages.reportGenerationFailed'))
    } finally {
      setGenerating(false)
    }
  }

  const columns = [
    {
      width: '12%',
      name: t('salesReports.columns.generatedBy'),
      key: 'generatedBy',
      rendered: item => <span className="text-[13px]">{item?.generatedBy === 'system' ? t('salesReports.columns.system') : item?.user?.fullName || '—'}</span>,
    },
    {
      width: '10%',
      name: t('salesReports.columns.reportType'),
      key: 'type',
      rendered: item => <span className="text-[13px]">{item?.type}</span>,
    },
    {
      width: '12%',
      name: t('salesReports.columns.branch'),
      key: 'branch',
      rendered: item => <span className="text-[13px]">{item?.branch?.name || t('salesReports.columns.allBranches')}</span>,
    },
    {
      width: '14%',
      name: t('salesReports.columns.dateRange'),
      key: 'range',
      rendered: item => {
        const formatDate = dateStr => {
          if (!dateStr) return ''
          return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        }
        return (
          <span className="text-[13px]">
            {item?.startDate || item?.endDate ? `${formatDate(item?.startDate)} - ${formatDate(item?.endDate)}` : t('salesReports.columns.allDates')}
          </span>
        )
      },
    },
    {
      name: t('salesReports.columns.salesReport'),
      width: '12%',
      key: 'download',
      rendered: item => {
        const reportId = item._id || item.id
        if (!reportId) return null
        const format = item?.pdfUrl ? 'pdf' : item?.excelUrl ? 'excel' : 'pdf'
        return (
          <button type="button" onClick={() => handleReportDownload(reportId, format)} className="text-sm text-blue-400 hover:text-blue-600 underline cursor-pointer">
            {t('salesReports.columns.downloadReport')}
          </button>
        )
      },
    },
    {
      width: '8%',
      name: t('salesReports.columns.created'),
      key: 'created',
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        const createdAt = createdTime ? moment(createdTime).fromNow() : '—'
        return (
          <div className="text-sm text-gray-500">
            <Tooltip title={createdTime ? formateDate(moment(createdTime)) : 'N/A'}>{createdAt}</Tooltip>
          </div>
        )
      },
    },
    {
      width: '8%',
      name: t('salesReports.columns.updated'),
      key: 'updated',
      rendered: item => {
        const updatedTime = get(item, 'updatedAt', '')
        const updatedAt = updatedTime ? moment(updatedTime).fromNow() : '—'
        return (
          <div className="text-sm text-gray-500">
            <Tooltip title={updatedTime ? formateDate(moment(updatedTime)) : 'N/A'}>{updatedAt}</Tooltip>
          </div>
        )
      },
    },
  ]

  return (
    <Layout
      headerRight={
        <div className="flex items-center justify-end gap-4">
          <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
        </div>
      }
      title={t('officeSalesReport.title')}
    >
      <div className="space-y-6 sm:space-y-5 pt-8">
        <p className="ml-2 md:ml-4 text-sm text-gray-500">{t('officeSalesReport.subtitle')}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 ml-2 md:ml-4">
          {isSuperCompanyUser ? (
            <div>
              <label htmlFor="branch" className="block text-sm font-normal text-gray-700 mb-1">
                {t('salesReports.form.branch')}
              </label>
              <Selection
                title={t('salesReports.form.selectBranch')}
                list={branches}
                onChange={value => setSelectedBranch(value?.id || null)}
                value={selectedBranch || ''}
                className="w-full"
                disabled={loadingBranches}
              />
            </div>
          ) : null}

          <div>
            <label htmlFor="date-range" className="block text-sm font-medium text-gray-700 mb-1">
              {t('salesReports.form.dateRange')}
            </label>
            <Popover
              custom
              placement="bottom-start"
              ref={popover}
              content={close => (
                <div className="max-w-[90vw] sm:max-w-none">
                  <DateRangePicker
                    ranges={[
                      {
                        startDate: dateRange.startDate ? dateRange.startDate.toDate() : new Date(),
                        endDate: dateRange.endDate ? dateRange.endDate.toDate() : new Date(),
                        key: 'selection',
                      },
                    ]}
                    onChange={handleSelect}
                  />
                  <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
                    <button
                      type="button"
                      onClick={close}
                      className="w-full sm:w-[100px] py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none"
                    >
                      {t('salesReports.buttons.close')}
                    </button>
                    <button
                      type="button"
                      disabled={!dateRange.startDate && !dateRange.endDate}
                      onClick={() => {
                        setDateRange({ startDate: null, endDate: null })
                        close()
                      }}
                      className="w-full sm:w-[100px] py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none"
                    >
                      {t('salesReports.buttons.clear')}
                    </button>
                  </div>
                </div>
              )}
            >
              <button
                id="date-range"
                type="button"
                className="inline-flex w-full h-[38px] justify-between items-center border border-gray-300 shadow-sm text-sm leading-4 font-regular rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none px-3"
              >
                <div className="flex items-center">
                  <IoCalendar className="h-4 w-4 mr-2 text-gray-500 flex-shrink-0" />
                  <span className="truncate">
                    {dateRange.startDate && dateRange.endDate
                      ? `${dateRange.startDate.format('MM-DD-YYYY')} to ${dateRange.endDate.format('MM-DD-YYYY')}`
                      : t('salesReports.form.selectDateRange')}
                  </span>
                </div>
                <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
              </button>
            </Popover>
          </div>
        </div>

        <div className="mt-3 ml-2 md:ml-4">
          <Button onClick={generateSalesReport} disabled={generating} className="w-full sm:w-auto">
            {generating ? t('salesReports.buttons.generatingReport') : t('salesReports.buttons.generateReport')}
          </Button>
        </div>

        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full overflow-x-auto">
                {!loading ? (
                  reports?.length === 0 ? (
                    <EmptyState v2 description={t('officeSalesReport.messages.noReportsFound')} />
                  ) : (
                    <div className="min-w-[800px]">
                      <Table
                        hasMore={pagination?.currentPage !== pagination?.totalPages}
                        fetchData={() => fetchReports(pagination?.currentPage + 1)}
                        data={reports}
                        columns={columns}
                      />
                    </div>
                  )
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  )
}

export default OfficeSalesReport
